import type { BoardCard, Stage } from "@salesboard/contracts";

export type DropTarget = { stageId: string; quarter: number };

/**
 * What the move flow needs to know about an opportunity.
 *
 * Narrower than `BoardCard` on purpose: the detail page reuses this flow, and it holds an
 * `Opportunity` with no `stalled` or `stageAgeDays` — values derived in SQL that no client
 * may invent. Asking for less means it does not have to.
 */
export type Movable = Pick<
  BoardCard,
  | "id"
  | "name"
  | "clientName"
  | "stageId"
  | "quarter"
  | "year"
  | "status"
  | "version"
  | "ownerId"
  | "ownerName"
  // Carried so a Won → Lost correction can offer the date already recorded rather than
  // defaulting to today and moving the outcome by however long the correction took.
  | "actualCloseDate"
>;

/**
 * What a drop means. Pure, because it is the board's most branch-heavy decision and the
 * one most worth testing without a browser.
 *
 * Ordering matters, and each step earns its place: the same-cell check comes first so a
 * no-op never sends a request; a move along the card's own row is a quarter move whatever
 * that row is (see below — this one was a bug); and closure is checked before a plain stage
 * move because a closed target needs a date the gesture cannot supply.
 */
export type DropIntent =
  | { kind: "none" }
  | { kind: "stage"; stageId: string }
  | { kind: "quarter"; quarter: number }
  | { kind: "stage-and-quarter"; stageId: string; quarter: number }
  | { kind: "close"; stageId: string; closedType: "won" | "lost"; quarter: number }
  | { kind: "reopen"; stageId: string; quarter: number };

export function classifyDrop(card: Movable, target: DropTarget, stages: Stage[]): DropIntent {
  const targetStage = stages.find((stage) => stage.id === target.stageId);
  if (!targetStage) return { kind: "none" };

  const stageChanged = card.stageId !== target.stageId;
  const quarterChanged = card.quarter !== target.quarter;

  // Nothing moved: no request, no audit event, no toast.
  if (!stageChanged && !quarterChanged) return { kind: "none" };

  /**
   * A move along the card's own row is a **quarter** move, whatever that row is.
   *
   * This test comes before the closed-stage test on purpose. Without it, dragging a Won card
   * from Q2 to Q3 of the Closed Won row read as "close it again": it re-ran the closure
   * dialog, overwrote the actual close date with today's default, wrote a second
   * `closed_won` event, and left the card in Q2 because a closure carries no expected date.
   * A card's column follows its expected close date, so dragging it sideways means exactly
   * one thing — change that date — regardless of whether the row happens to be a closed one.
   */
  if (!stageChanged) return { kind: "quarter", quarter: target.quarter };

  /**
   * Between two closed stages of the **same** outcome — "Closed Lost — No budget" to
   * "Closed Lost — Competitor".
   *
   * A recategorisation, not a re-closure. The deal closed once, on the date it closed;
   * running the closure dialog again would ask for an actual close date it already has and
   * write a second `closed_lost` for one closure. Reachable only since stages became
   * configurable — before that each outcome had exactly one stage, so this drop was
   * impossible and the branch below was complete.
   */
  if (targetStage.closedType !== "open" && card.status === targetStage.closedType) {
    // Diagonally: the drop also names a quarter, and dropping a card in Q3 must not leave
    // it in Q2. The server accepts an expected close date on a recategorising move, so the
    // date prompt runs exactly as it does for any other cross-quarter drag.
    return quarterChanged
      ? { kind: "stage-and-quarter", stageId: target.stageId, quarter: target.quarter }
      : { kind: "stage", stageId: target.stageId };
  }

  // Into a closed stage — including Won → Lost, which is a correction, not a no-op.
  if (targetStage.closedType !== "open") {
    return { kind: "close", stageId: target.stageId, closedType: targetStage.closedType, quarter: target.quarter };
  }

  // Out of a closed stage into an open one.
  if (card.status !== "open") {
    return { kind: "reopen", stageId: target.stageId, quarter: target.quarter };
  }

  if (quarterChanged) {
    return { kind: "stage-and-quarter", stageId: target.stageId, quarter: target.quarter };
  }
  return { kind: "stage", stageId: target.stageId };
}

/**
 * Whether the intent needs a dialog before anything is sent.
 *
 * This is what makes "cancel writes nothing" structural rather than disciplined: for these
 * intents no request exists until the dialog is confirmed.
 */
export function needsDialog(intent: DropIntent): boolean {
  // Only the two that need a fact the drop does not state: a closure's *actual* close date,
  // and a reopening's new expected one. A cross-quarter drop stated its quarter by landing
  // there, and takes that quarter's last day (2026-08-25, specs/drag-and-activity.md) — the
  // date prompt it used to open asked for something the gesture had already said.
  return intent.kind === "close" || intent.kind === "reopen";
}
